import gsap from 'gsap'
import ScrollToPlugin from 'gsap/ScrollToPlugin'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

export default class CompanyStackCards {
	constructor() {
		gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)

		this.cards = [...document.querySelectorAll('[data-stack-cards]')]

		if (!this.cards) return

		this.endTrigger = document.querySelector('[data-stack-cards-container]')

		if (!this.endTrigger) return

		this.titleHeight = 0
		this.topPadding = 0
		this.scrollTriggers = []

		void this.#init()
		void this.#onResize()
		void this.#onTitleClick()
		void this.#onButtonHover()
	}

	#init() {
		void this.#recalculate()
	}

	#recalculate() {
		void this.#killTriggers()
		void this.#resetStyles()
		void this.#calculateTitleHeight()
		void this.#calculateTopPadding()
		void this.#setUpTriggers()
	}

	#killTriggers() {
		this.scrollTriggers.forEach(st => st.kill())
		this.scrollTriggers = []
	}

	#resetStyles() {
		this.cards.forEach(card => {
			const inner = card.querySelector('[data-stack-cards-inner]')
			if (inner) gsap.set(inner, { clearProps: 'y' })
			gsap.set(card, { clearProps: 'all' })
		})
	}

	#onResize() {
		let resizeTimer
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimer)
			resizeTimer = setTimeout(() => {
				this.#recalculate()
			}, 250)
		})
	}

	#onTitleClick() {
		this.cards.forEach((card, index) => {
			const title = card.querySelector('[data-stack-cards-title]')
			if (!title) return

			title.addEventListener('click', () => {
				const st = this.scrollTriggers[index]

				if (!st) return

				gsap.to(window, {
					scrollTo: st.start,
					duration: 0.3,
					ease: 'none',
				})
			})
		})
	}

	#onButtonHover() {
		this.cards.forEach(card => {
			const button = card.querySelector('[data-open-subcompany-menu]')
			const inner = card.querySelector('[data-stack-cards-inner]')
			if (!button || !inner) return

			const spans = inner.querySelectorAll('span')
			const tl = gsap.timeline({ paused: true })
			tl.to(inner, { backgroundColor: '#003D77', color: '#FFFFFF', duration: 0.5 })
			tl.to(spans, { color: '#FFFFFF', duration: 0.5 }, 0)

			button.addEventListener('mouseenter', () => tl.play())
			button.addEventListener('mouseleave', () => tl.reverse())
		})
	}

	#calculateTitleHeight() {
		const title = this.cards[0]?.querySelector('[data-stack-cards-title]')
		this.titleHeight = title?.getBoundingClientRect()?.height || 0
	}

	#calculateTopPadding() {
		this.topPadding = this.cards[0]?.querySelector('[data-stack-cards-inner]')
			? parseFloat(getComputedStyle(this.cards[0].querySelector('[data-stack-cards-inner]')).paddingTop)
			: 0
	}

	#setUpTriggers() {
		let i = 0
		for (const card of this.cards) {
			const offset = this.#calculatePinOffset(i)

			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: card,
					pin: true,
					start: `top top+=${offset}px`,
					end: `bottom bottom`,
					endTrigger: this.endTrigger,
					pinSpacing: false,
					pinSpacer: false,
					scrub: true,
				},
			})
			this.scrollTriggers.push(tl.scrollTrigger)
			i++
		}

		const cardHeight = this.cards[0].getBoundingClientRect().height
		const totalOffset = this.#calculatePinOffset(this.cards.length - 1)
		const yAmount = Math.max(0, totalOffset + cardHeight - window.innerHeight)

		if (yAmount === 0) return

		const tl = gsap
			.timeline({
				scrollTrigger: {
					trigger: this.endTrigger,
					start: `top top`,
					endTrigger: this.endTrigger,
					end: 'bottom bottom',
					scrub: true,
				},
			})
			.to('[data-stack-cards-inner]', { y: -yAmount, ease: 'none' })
		this.scrollTriggers.push(tl.scrollTrigger)
	}

	#calculatePinOffset(i) {
		return i * this.titleHeight + 2 * i * this.topPadding
	}
}
